import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useI18n } from "../i18n";
import { MaskLines, Reveal, RevealLines } from "../components/Reveal";
import Marquee from "../components/Marquee";
import { api } from "../lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1568101671082-dbd18c6a9b31?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

function Hero() {
  const { t } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section ref={ref} className="relative h-screen overflow-hidden bg-[#062029]" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Belgrade skyline at dusk" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-[#062029]" />
      <div className="absolute inset-0 grain" />

      <div className="relative h-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-20 md:pb-28">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="label text-[#C5A880] mb-8"
        >
          {t.hero.kicker}
        </motion.p>
        <h1 className="font-serif-display text-[#F7F5F0] text-5xl sm:text-7xl lg:text-8xl leading-[0.98] tracking-tight max-w-5xl">
          <MaskLines lines={[t.hero.line1, t.hero.line2, t.hero.line3]} delay={0.35} />
        </h1>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 1 }}
          className="mt-10 flex flex-col md:flex-row md:items-end justify-between gap-8"
        >
          <p className="font-body text-[#F7F5F0]/80 text-base md:text-lg max-w-md leading-relaxed">
            {t.hero.sub}
          </p>
          <Link
            to="/contact"
            data-testid="hero-cta"
            className="group inline-flex items-center gap-3 border border-[#C5A880]/60 text-[#F7F5F0] px-7 py-4 text-[12px] tracking-widest uppercase hover:bg-[#C5A880] hover:text-[#062029] transition-colors duration-500 whitespace-nowrap"
          >
            {t.hero.cta}
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function Intro() {
  const { t } = useI18n();
  return (
    <section className="bg-[#F7F5F0] py-28 md:py-40" data-testid="intro">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-3">
          <Reveal><p className="label text-[#C5A880]">{t.intro.label}</p></Reveal>
        </div>
        <div className="md:col-span-8">
          <RevealLines
            className="font-serif-display text-[#062029] text-3xl md:text-5xl lg:text-[3.4rem] leading-[1.1]"
            lines={t.intro.heading.split(". ").map((s, i, a) => (i < a.length - 1 ? s + "." : s))}
          />
          <Reveal delay={0.2}>
            <p className="mt-10 font-body text-[#2A2A2A]/75 text-lg leading-relaxed max-w-2xl">{t.intro.body}</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Manifesto() {
  const { t } = useI18n();
  return (
    <section className="bg-[#062029] relative grain overflow-hidden py-28 md:py-40" data-testid="manifesto">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal><p className="label text-[#C5A880] mb-16 md:mb-24">{t.manifesto.label}</p></Reveal>
        <div className="space-y-20 md:space-y-28">
          {t.manifesto.items.map((item, i) => (
            <div key={i} className="grid md:grid-cols-12 gap-8 items-start border-t border-[#F7F5F0]/12 pt-12">
              <div className="md:col-span-3">
                <Reveal>
                  <span className="font-serif-display text-[#C5A880] text-6xl md:text-7xl">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </Reveal>
              </div>
              <div className="md:col-span-6">
                <RevealLines
                  className="font-serif-display text-[#F7F5F0] text-3xl md:text-5xl leading-tight"
                  lines={[item.t]}
                />
                <Reveal delay={0.15}>
                  <p className="mt-6 font-body text-[#F7F5F0]/70 leading-relaxed">{item.d}</p>
                </Reveal>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GatewaysPreview() {
  const { t, lang } = useI18n();
  const [gateways, setGateways] = useState([]);
  useEffect(() => {
    api.get("/gateways").then((r) => setGateways(r.data)).catch(() => {});
  }, []);
  const featured = gateways.find((g) => g.featured) || gateways[0];

  return (
    <section className="bg-[#F7F5F0] py-28 md:py-40" data-testid="gateways-preview">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-3"><Reveal><p className="label text-[#C5A880]">{t.gateways.label}</p></Reveal></div>
          <div className="md:col-span-8">
            <RevealLines className="font-serif-display text-[#062029] text-3xl md:text-5xl leading-tight" lines={[t.gateways.heading]} />
            <Reveal delay={0.15}><p className="mt-8 font-body text-[#2A2A2A]/75 text-lg max-w-2xl leading-relaxed">{t.gateways.body}</p></Reveal>
          </div>
        </div>

        {featured && (
          <Reveal>
            <Link to="/gateways" data-testid="gateway-featured" className="group block relative overflow-hidden">
              <div className="relative h-[60vh] md:h-[75vh] overflow-hidden">
                <img src={featured.image} alt={featured.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.4s] ease-out" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#062029]/85 via-[#062029]/20 to-transparent" />
                <div className="absolute bottom-0 left-0 p-8 md:p-14">
                  <span className="label text-[#C5A880]">{t.gateways.featured}</span>
                  <h3 className="font-serif-display text-[#F7F5F0] text-5xl md:text-7xl mt-4">{featured.name}</h3>
                  <p className="font-body text-[#F7F5F0]/80 mt-4 max-w-lg">{lang === "de" ? featured.tagline_de : featured.tagline_en}</p>
                  <span className="inline-flex items-center gap-2 text-[#F7F5F0] mt-8 label group-hover:text-[#C5A880] transition-colors duration-300">
                    {t.gateways.view} <ArrowUpRight size={16} />
                  </span>
                </div>
              </div>
            </Link>
          </Reveal>
        )}
      </div>
    </section>
  );
}

function ServicesPreview() {
  const { t } = useI18n();
  const [active, setActive] = useState(0);
  const images = [
    "https://images.unsplash.com/photo-1655919640606-28088c64edb0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    "https://images.unsplash.com/photo-1780863169728-29e9af6f70a2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    "https://images.unsplash.com/photo-1771270759486-1f7703945072?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    "https://images.unsplash.com/photo-1598928506311-c55ded91a20c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  ];
  return (
    <section className="bg-[#062029] relative grain overflow-hidden py-28 md:py-40" data-testid="services-preview">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="mb-16 max-w-3xl">
          <Reveal><p className="label text-[#C5A880]">{t.services.label}</p></Reveal>
          <RevealLines className="font-serif-display text-[#F7F5F0] text-3xl md:text-5xl leading-tight mt-6" lines={[t.services.heading]} />
        </div>
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            {t.services.list.map((s, i) => (
              <Link
                to="/services"
                key={i}
                onMouseEnter={() => setActive(i % images.length)}
                data-testid={`service-item-${i}`}
                className="group flex items-baseline justify-between border-b border-[#F7F5F0]/12 py-6 hover:pl-3 transition-all duration-500"
              >
                <span className="font-serif-display text-[#F7F5F0] text-2xl md:text-4xl group-hover:text-[#C5A880] transition-colors duration-300">{s.t}</span>
                <ArrowUpRight size={20} className="text-[#C5A880] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </Link>
            ))}
          </div>
          <div className="md:col-span-5 hidden md:block">
            <div className="sticky top-28 h-[70vh] overflow-hidden">
              {images.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
                  style={{ opacity: active === i ? 1 : 0 }}
                />
              ))}
              <div className="absolute inset-0 bg-[#062029]/15" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { t } = useI18n();
  return (
    <>
      <Hero />
      <Intro />
      <Marquee items={t.marquee} />
      <Manifesto />
      <GatewaysPreview />
      <ServicesPreview />
    </>
  );
}
